import { Button } from '@cibli/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, ChevronDown, Globe, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import LanguageSelectModal from './LanguageSelectModal';

interface HeaderLanguageSelectorProps {
  className?: string;
}

// Map known codes to human-readable full native labels
const LOCALE_FULL_NAMES: Record<string, string> = {
  'fr-FR': 'Français',
  fr: 'Français',
  'es-MX': 'Español (México)',
  'es-ES': 'Español (España)',
  es: 'Español',
  'en-GB': 'English (UK)',
  'en-US': 'English (US)',
  en: 'English',
  'de-DE': 'Deutsch',
  de: 'Deutsch',
  'pt-PT': 'Português',
  pt: 'Português',
  'uk-UA': 'Українська',
  uk: 'Українська',
  'ar-MA': 'العربية (المغرب)',
  'ar-DZ': 'العربية (الجزائر)',
  'ro-RO': 'Română',
  ro: 'Română',
  'tr-TR': 'Türkçe',
  tr: 'Türkçe',
};

export default function HeaderLanguageSelector({ className }: HeaderLanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [showCallout, setShowCallout] = useState(false);

  // Derive country code for flag (e.g. 'fr-FR' -> 'FR', 'es-MX' -> 'MX', 'en' -> 'GB')
  const currentLang = i18n.language || 'fr-FR';
  const parts = currentLang.split('-');
  const countryCode =
    parts[1]?.toUpperCase() ||
    (parts[0] === 'en'
      ? 'GB'
      : parts[0] === 'fr'
        ? 'FR'
        : parts[0] === 'es'
          ? 'ES'
          : parts[0] === 'de'
            ? 'DE'
            : parts[0] === 'pt'
              ? 'PT'
              : parts[0] === 'uk'
                ? 'UA'
                : parts[0] === 'ar'
                  ? 'MA'
                  : parts[0]?.toUpperCase());

  const fullLanguageName =
    LOCALE_FULL_NAMES[currentLang] || LOCALE_FULL_NAMES[parts[0]] || currentLang.toUpperCase();

  // Show a welcoming visual callout with an arrow guiding the user
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowCallout(true);
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative inline-flex items-center">
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setShowCallout(false);
          setModalOpen(true);
        }}
        className={`h-12 sm:h-14 px-4 sm:px-5 rounded-full border-2 border-black/15 bg-background hover:bg-accent/80 backdrop-blur-md flex items-center gap-3 text-sm sm:text-base font-bold transition-all shadow-sm hover:shadow-md cursor-pointer ${className ?? ''}`}
        aria-label="Changer de langue"
      >
        {countryCode ? (
          <div className="size-6 sm:size-7 rounded-full overflow-hidden flex items-center justify-center shrink-0 border border-black/15 shadow-2xs">
            <ReactCountryFlag
              countryCode={countryCode}
              svg
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
          </div>
        ) : (
          <Globe className="size-5 shrink-0 text-muted-foreground" />
        )}

        <span className="font-bold text-foreground tracking-normal">{fullLanguageName}</span>
        <ChevronDown className="size-4 opacity-70 shrink-0 stroke-[2.5]" />
      </Button>

      {/* Guide visuel animé avec flèche pour indiquer le choix de langue */}
      <AnimatePresence>
        {showCallout && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute top-full right-2 mt-2 z-50 flex flex-col items-end pointer-events-auto"
          >
            {/* Flèche pointant vers le haut / le bouton */}
            <div className="mr-6 flex items-center justify-center">
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <ArrowUp className="size-5 text-[#21c294] stroke-[3]" />
              </motion.div>
            </div>

            {/* Bulle d'indication en vert Cibli agrandie et bien visible */}
            <div className="flex items-center gap-2.5 bg-[#21c294] text-white pl-4 pr-2 py-2 rounded-full shadow-xl shadow-[#21c294]/30 border-2 border-white text-sm font-bold select-none">
              <span className="text-lg shrink-0 leading-none">🌍</span>
              <span className="text-sm font-black tracking-wide whitespace-nowrap">
                Choix de langue
              </span>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setShowCallout(false);
                }}
                className="size-6 rounded-full hover:bg-black/20 flex items-center justify-center text-white shrink-0 transition-colors ml-1 cursor-pointer"
                aria-label="Fermer l'indication"
              >
                <X className="size-3.5 stroke-[2.5]" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <LanguageSelectModal open={modalOpen} onOpenChange={setModalOpen} />
    </div>
  );
}
